import { channel, END, EventChannel, eventChannel } from "@redux-saga/core";
import {
    fork,
    join,
    put,
    select,
    take,
    takeLeading,
} from "@redux-saga/core/effects";
import { all } from "redux-saga/effects";
import { post } from "sagas/helpers/network";
import axios from "axios";
import createAnimationUploadVideoStatusSelector from "selectors/createAnimationUploadVideoStatus";

const uploadProgressChannel = channel();

function uploadEmitter(
    uploadUrl: string,
    file: File
): EventChannel<sage.actions.createAnimation.ReducerTypes> {
    const data = new FormData();
    data.append("file", file);

    return eventChannel((emit) => {
        axios
            .put(uploadUrl, file, {
                onUploadProgress: (progressEvent: {
                    loaded: number;
                    total: number;
                }) => {
                    const percentCompleted =
                        progressEvent.loaded / progressEvent.total;
                    emit({
                        type:
                            "com.sage.actions.createAnimation.ISetUploadVideoProgress",
                        progress: percentCompleted,
                    } as sage.actions.createAnimation.ISetUploadVideoProgress);
                },
            })
            .then(() => {
                emit({
                    type:
                        "com.sage.actions.createAnimation.ISetUploadVideoProgress",
                    progress: 1,
                } as sage.actions.createAnimation.ISetUploadVideoProgress);
                emit(END);
            })
            .catch(() => {
                emit({
                    type:
                        "com.sage.actions.createAnimation.ISetUploadFileStatus",
                    status: "error",
                } as sage.actions.createAnimation.ISetUploadVideoFileStatus);
                emit(END);
            });
        // eslint-disable-next-line @typescript-eslint/no-empty-function
        return () => {};
    });
}

function* uploadListener(
    chan: EventChannel<sage.actions.createAnimation.ReducerTypes>
) {
    while (true) {
        const data = yield take(chan);
        yield put(data);
    }
}

function* execute(action: sage.actions.createAnimation.IUploadVideoFile) {
    yield put({
        type: "com.sage.actions.createAnimation.ISetUploadFileStatus",
        status: "loading",
    } as sage.actions.createAnimation.ISetUploadVideoFileStatus);
    yield put({
        type: "com.sage.actions.createAnimation.ISetUploadVideoProgress",
        progress: 0,
    } as sage.actions.createAnimation.ISetUploadVideoProgress);
    const response = yield post("/create-video-file", {
        original_name: action.file.name,
        is_front_facing: action.isFrontFacingCamera,
    });
    yield put({
        type: "com.sage.actions.createAnimation.ISetUploadFileStatus",
        status: "uploading",
    } as sage.actions.createAnimation.ISetUploadVideoFileStatus);
    yield put({
        type: "com.sage.actions.createAnimation.ISetUploadVideoProgress",
        progress: 0,
    } as sage.actions.createAnimation.ISetUploadVideoProgress);

    const emitter = uploadEmitter(response.data.upload_url, action.file);

    const task = yield fork(uploadListener, emitter);

    yield join(task);

    const currentType: sage.status.UploadVideoFileStatus = yield select(
        createAnimationUploadVideoStatusSelector
    );

    if (currentType === "error") {
        // We had an issue in the upload file part.
        return;
    }

    yield put({
        type: "com.sage.actions.createAnimation.ISetUploadFileStatus",
        status: "creatingJob",
    } as sage.actions.createAnimation.ISetUploadVideoFileStatus);

    yield post("/update-video-file", {
        video_file_id: response.data.id,
        file_state: "UPLOADED",
    });

    const jobResponse = yield post("/create-video-job", {
        video_job_type: action.animationType,
        video_file_id: response.data.id,
    });

    if (
        jobResponse === null ||
        jobResponse.data === null ||
        jobResponse.data.id === null
    ) {
        yield put({
            type: "com.sage.actions.createAnimation.ISetUploadFileStatus",
            status: "error",
        } as sage.actions.createAnimation.ISetUploadVideoFileStatus);
        return;
    }

    yield put({
        type: "com.sage.actions.createAnimation.ISetVideoJobID",
        videoJobId: jobResponse.data.id,
    } as sage.actions.createAnimation.ISetVideoJobID);

    yield put({
        type: "com.sage.actions.createAnimation.ISetUploadFileStatus",
        status: "finished",
    } as sage.actions.createAnimation.ISetUploadVideoFileStatus);
}

export default function* saga(): Generator {
    yield all([
        takeLeading(
            "com.sage.actions.createAnimation.IUploadVideoFile",
            execute
        ),
        take(uploadProgressChannel),
    ]);
}
